import { useEffect, useMemo, useRef, useState } from 'react'
import { DragDropContext, type DropResult } from '@hello-pangea/dnd'
import { Button } from '@/components/ui/button'
import ProjectTaskBoard from '@/frontend/features/Fde/ProjectTaskBoard'
import ProjectTaskList from '@/frontend/features/Fde/ProjectTaskList'
import TaskCategoryGroups from '@/frontend/features/Fde/TaskCategoryGroups'
import ProjectTaskGantt from '@/frontend/features/Fde/ProjectTaskGantt'
import ProjectTaskSchedule from '@/frontend/features/Fde/ProjectTaskSchedule'
import { taskStatuses } from '@/frontend/features/Fde/projectTaskPresentation'
import { taskListGroups, taskListDropTarget } from '@/frontend/features/Fde/taskListTree'
import type { TaskPatch } from '@/frontend/features/Fde/useProjectTaskActions'
import type { ProjectManagementView } from '@/frontend/features/Fde/managementTypes'
import type { FdeCopy, ProjectTask, ProjectTaskAssignee } from '@/frontend/features/Fde/types'

export interface TaskSurfaceProps {
  tasks: ProjectTask[]
  overdueIds: Set<string>
  onSelect: (id: string) => void
  canManage: boolean
  busy: boolean
  copy: FdeCopy
  onPatch: (task: ProjectTask, changes: TaskPatch) => void
  onCopy: (task: ProjectTask) => void
}
interface Props extends TaskSurfaceProps {
  allTasks: ProjectTask[]
  filterKey: string
  groupBy?: 'status' | 'category'
  projectId: string
  canViewEvents: boolean
  canViewFiles: boolean
  calendarQuery: string
  calendarPerson?: ProjectTaskAssignee
  view: ProjectManagementView
  total: number
  today: string
  monthOffset: number
  onMonth: (offset: number) => void
  onMove: (task: ProjectTask, status: ProjectTask['status'], beforeId: string | null) => void
  onReset: () => void
  onDragStart: () => void
  onDragFinish: () => void
}

export default function ProjectTaskCanvas(props: Props) {
  const dragged = useRef<ProjectTask | null>(null)
  const scope = `${props.filterKey}:${props.groupBy ?? 'status'}`
  const [collapsed, setCollapsed] = useState<{ scope: string; ids: Set<string> }>({
    scope,
    ids: new Set(),
  })
  const closed = collapsed.scope === scope ? collapsed.ids : new Set<string>()
  // Polls preserve manual collapse. A changed filter starts expanded so a
  // matching descendant is not trapped inside an old, hidden branch.
  useEffect(() => setCollapsed({ scope, ids: new Set() }), [scope])
  const groups = useMemo(
    () =>
      taskListGroups(
        props.tasks,
        props.allTasks,
        props.groupBy ?? 'status',
        closed,
        props.copy.management,
        taskStatuses,
      ),
    [props.tasks, props.allTasks, props.groupBy, closed, props.copy.management],
  )
  function toggleTask(id: string) {
    const ids = new Set(closed)
    if (ids.has(id)) ids.delete(id)
    else ids.add(id)
    setCollapsed({ scope, ids })
  }
  function finish(result: DropResult) {
    const task = dragged.current
    dragged.current = null
    props.onDragFinish()
    const { source, destination } = result
    if (
      !task ||
      !destination ||
      (source.droppableId === destination.droppableId && source.index === destination.index)
    )
      return
    if (destination.droppableId.startsWith('category:')) {
      const categoryId = destination.droppableId.slice('category:'.length) || null
      if (categoryId !== (task.task_category_id ?? null))
        props.onPatch(task, { task_category_id: categoryId })
      return
    }
    const status = destination.droppableId as ProjectTask['status']
    if (!taskStatuses.includes(status)) return
    if (props.view === 'list') {
      const target = groups.find((group) => group.id === destination.droppableId)
      if (!target) return
      const move = taskListDropTarget(
        target,
        task,
        destination.index,
        source.droppableId === destination.droppableId,
      )
      props.onMove(task, move.status, move.beforeId)
      return
    }
    const group = props.tasks.filter((item) => item.status === status && item.id !== task.id)
    props.onMove(task, status, group[destination.index]?.id ?? null)
  }
  if (props.view === 'schedule')
    return <ProjectTaskSchedule {...props} offset={props.monthOffset} onOffset={props.onMonth} />
  if (!props.tasks.length)
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
        <p className="text-sm font-medium">
          {props.total ? props.copy.management.empty_filter : props.copy.management.empty_project}
        </p>
        <p className="max-w-xs text-xs leading-5 text-muted-foreground">
          {props.total
            ? props.copy.management.select_task_hint
            : props.copy.management.empty_project_hint}
        </p>
        {props.total > 0 && (
          <Button variant="outline" size="sm" onClick={props.onReset}>
            {props.copy.management.reset_filters}
          </Button>
        )}
      </div>
    )
  if (props.view === 'gantt')
    return <ProjectTaskGantt {...props} offset={props.monthOffset} onOffset={props.onMonth} />
  return (
    <DragDropContext
      onBeforeDragStart={(start) => {
        dragged.current = props.tasks.find((task) => task.id === start.draggableId) ?? null
        props.onDragStart()
      }}
      onDragEnd={finish}
      dragHandleUsageInstructions={props.copy.management.drag_instructions}
    >
      {/* Independent columns use one scroll parent each. Narrow boards share
          a single scroller so drag-and-drop never nests scroll containers. */}
      <div className="@container h-full min-h-0">
        {props.view === 'list' ? (
          <ProjectTaskList {...props} groups={groups} collapsed={closed} onToggle={toggleTask} />
        ) : props.groupBy === 'category' ? (
          <TaskCategoryGroups key={props.projectId} {...props} board={props.view === 'board'} />
        ) : props.view === 'board' ? (
          <ProjectTaskBoard {...props} />
        ) : null}
      </div>
    </DragDropContext>
  )
}
